const DB_NAME = 'fazenda-hostgator-sync';
const DB_VERSION = 3;
const STORE = 'queue';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (!("indexedDB" in window)) {
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'opId' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('entityKey', 'entityKey', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('nextAttemptAt', 'nextAttemptAt', { unique: false });
        } else {
          const tx = req.transaction;
          const store = tx.objectStore(STORE);
          if (!store.indexNames.contains('nextAttemptAt')) store.createIndex('nextAttemptAt', 'nextAttemptAt', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function localRead() {
  try {
    return JSON.parse(localStorage.getItem('syncQueue') || '[]');
  } catch {
    return [];
  }
}

function localWrite(queue) {
  try {
    localStorage.setItem('syncQueue', JSON.stringify(queue));
  } catch {}
}

async function withStore(mode, fn) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      Promise.resolve(fn(store, tx)).then(resolve).catch(() => resolve(null));
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function computeBackoff(retries = 0) {
  if (retries <= 0) return 2000;
  if (retries === 1) return 5000;
  if (retries === 2) return 10000;
  if (retries === 3) return 30000;
  return 60000;
}


function mergePendingOps(existing, incoming) {
  const existingType = String(existing?.type || existing?.action || '').toLowerCase();
  const incomingType = String(incoming?.type || incoming?.action || '').toLowerCase();

  if (!existing || existing.status === 'done') return incoming;
  if (!incoming) return existing;

  if (existingType === 'create' && incomingType === 'edit') {
    return {
      ...existing,
      ...incoming,
      type: 'create',
      changes: { ...(existing.changes || {}), ...(incoming.changes || {}) },
      payload: { ...(existing.payload || {}), ...(incoming.payload || {}) },
      timestamp: incoming.timestamp || existing.timestamp,
      nextAttemptAt: Math.min(existing.nextAttemptAt || Date.now(), incoming.nextAttemptAt || Date.now()),
      retries: Math.min(existing.retries || 0, incoming.retries || 0),
    };
  }

  if (existingType === 'edit' && incomingType === 'edit') {
    return {
      ...existing,
      ...incoming,
      type: 'edit',
      changes: { ...(existing.changes || {}), ...(incoming.changes || {}) },
      payload: { ...(existing.payload || {}), ...(incoming.payload || {}) },
      timestamp: incoming.timestamp || existing.timestamp,
      nextAttemptAt: Math.min(existing.nextAttemptAt || Date.now(), incoming.nextAttemptAt || Date.now()),
      retries: Math.min(existing.retries || 0, incoming.retries || 0),
    };
  }

  if ((existingType === 'create' || existingType === 'edit') && incomingType === 'delete') {
    if (existingType === 'create') {
      return {
        ...incoming,
        type: 'noop',
        status: 'done',
      };
    }
    return {
      ...existing,
      ...incoming,
      type: 'delete',
      payload: { ...(existing.payload || {}), ...(incoming.payload || {}) },
      timestamp: incoming.timestamp || existing.timestamp,
      nextAttemptAt: Math.min(existing.nextAttemptAt || Date.now(), incoming.nextAttemptAt || Date.now()),
      retries: Math.min(existing.retries || 0, incoming.retries || 0),
    };
  }

  if (existingType === 'delete' && incomingType === 'create') {
    return {
      ...existing,
      ...incoming,
      type: 'edit',
      changes: { ...(existing.changes || {}), ...(incoming.changes || {}) },
      payload: { ...(incoming.payload || {}) },
      timestamp: incoming.timestamp || existing.timestamp,
      nextAttemptAt: Math.min(existing.nextAttemptAt || Date.now(), incoming.nextAttemptAt || Date.now()),
      retries: Math.min(existing.retries || 0, incoming.retries || 0),
    };
  }

  return incoming;
}

function parseEntityKeyParts(entityKey = '') {
  const parts = String(entityKey || '').split(':');
  return {
    farmId: parts[0] || '',
    collection: parts[1] || '',
    itemId: parts.slice(2).join(':') || '',
  };
}

export const SyncQueue = {
  async list() {
    const rows = await withStore('readonly', (store) => new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    }));
    const data = Array.isArray(rows) ? rows : localRead();
    return data.sort((a, b) => (a.nextAttemptAt || a.timestamp || 0) - (b.nextAttemptAt || b.timestamp || 0));
  },

  async add(op) {
    const item = {
      opId: op.opId || `op_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: op.timestamp || Date.now(),
      nextAttemptAt: op.nextAttemptAt || Date.now(),
      status: op.status || 'pending',
      retries: op.retries || 0,
      entityKey: op.entityKey || `${op.farmId || 'global'}:${op.collection || op.entity || 'unknown'}:${op.itemId || 'all'}`,
      ...op,
    };

    const current = await this.list();
    const existing = current.find((row) => row.status !== 'done' && row.entityKey === item.entityKey);
    const nextItem = existing ? mergePendingOps(existing, item) : item;
    const deduped = current.filter((row) => !(row.status !== 'done' && row.entityKey === item.entityKey));
    if (nextItem.status !== 'done' && nextItem.type !== 'noop') deduped.push(nextItem);
    localWrite(deduped);

    await withStore('readwrite', (store) => {
      if (existing) store.delete(existing.opId);
      deduped.forEach((row) => store.put(row));
      return true;
    });
    return nextItem;
  },

  async claimReady(limit = 20) {
    const now = Date.now();
    const rows = await this.list();
    return rows.filter((row) => row.status !== 'done' && (row.nextAttemptAt || 0) <= now).slice(0, limit);
  },

  async markDone(opId) {
    const current = await this.list();
    const next = current.filter((row) => row.opId !== opId);
    localWrite(next);
    await withStore('readwrite', (store) => {
      store.delete(opId);
      return true;
    });
  },

  async clearEntity(entityKey) {
    const rows = await this.list();
    const next = rows.filter((row) => row.entityKey !== entityKey);
    localWrite(next);
    await withStore('readwrite', (store) => {
      rows.filter((row) => row.entityKey === entityKey).forEach((row) => store.delete(row.opId));
      return true;
    });
  },

  async bumpRetry(opId) {
    const current = await this.list();
    const idx = current.findIndex((row) => row.opId === opId);
    if (idx < 0) return null;
    const retries = (current[idx].retries || 0) + 1;
    current[idx] = {
      ...current[idx],
      retries,
      lastRetryAt: Date.now(),
      nextAttemptAt: Date.now() + computeBackoff(retries),
      status: 'pending',
    };
    localWrite(current);
    await withStore('readwrite', (store) => store.put(current[idx]));
    return current[idx];
  },

  async size() {
    const rows = await this.list();
    return rows.length;
  },

  async requeue(opId) {
    const current = await this.list();
    const idx = current.findIndex((row) => row.opId === opId);
    if (idx < 0) return null;
    current[idx] = {
      ...current[idx],
      status: 'pending',
      retries: 0,
      nextAttemptAt: Date.now(),
      requeuedAt: Date.now(),
    };
    localWrite(current);
    await withStore('readwrite', (store) => store.put(current[idx]));
    return current[idx];
  },

  async remove(opId) {
    return this.markDone(opId);
  },

  async stats() {
    const rows = await this.list();
    const now = Date.now();
    const summary = {
      total: rows.length,
      ready: 0,
      scheduled: 0,
      retrying: 0,
      overdue: 0,
      byCollection: {},
      items: rows.map((row) => ({
        opId: row.opId,
        status: row.status || 'pending',
        retries: row.retries || 0,
        nextAttemptAt: row.nextAttemptAt || row.timestamp || now,
        timestamp: row.timestamp || now,
        entityKey: row.entityKey || '',
        ...parseEntityKeyParts(row.entityKey || ''),
      })),
    };
    rows.forEach((row) => {
      const collection = parseEntityKeyParts(row.entityKey || '').collection || row.collection || 'geral';
      if (!summary.byCollection[collection]) summary.byCollection[collection] = { total: 0, retrying: 0, overdue: 0 };
      summary.byCollection[collection].total += 1;
      if ((row.nextAttemptAt || 0) <= now) summary.ready += 1;
      else summary.scheduled += 1;
      if ((row.retries || 0) > 0) {
        summary.retrying += 1;
        summary.byCollection[collection].retrying += 1;
      }
      if ((row.nextAttemptAt || 0) < now - 60000) {
        summary.overdue += 1;
        summary.byCollection[collection].overdue += 1;
      }
    });
    return summary;
  },

};
